import { useMemo, useContext } from 'react';
import { useSelector } from 'react-redux';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import {
  getPackageConfig,
  getPickerMode,
} from '../../redux/packagePicker/selectors';
import {
  getPackagePickerConfigOrder,
  getRecommendedPackageType,
} from './utils';
import { CoreTypes } from './types';

export const usePackagePickerOrder = (): CoreTypes[] => {
  const packageConfig = useSelector(getPackageConfig);
  const pickerMode = useSelector(getPickerMode);
  const packagePickerOrder = getPackagePickerConfigOrder(
    packageConfig,
    pickerMode
  );
  return packagePickerOrder;
};

export const usePackagePickerOrderForMobile = (): CoreTypes[] => {
  const packageConfig = useSelector(getPackageConfig);
  const pickerMode = useSelector(getPickerMode);
  const isMobile = useContext(MobileContext);

  const packagePickerOrder = useMemo(() => {
    const originalOrder = getPackagePickerConfigOrder(
      packageConfig,
      pickerMode
    );

    // On mobile, put recommended package first
    if (isMobile) {
      const recommendedPackageType = getRecommendedPackageType(packageConfig);
      if (recommendedPackageType) {
        const recommendedIndex = originalOrder.findIndex(
          coreType => coreType === recommendedPackageType
        );
        if (recommendedIndex > 0) {
          // Remove recommended package from its current position and put it first
          const reordered = [
            recommendedPackageType,
            ...originalOrder.filter(
              coreType => coreType !== recommendedPackageType
            ),
          ];
          return reordered;
        }
      }
    }

    return originalOrder;
  }, [packageConfig, pickerMode, isMobile]);

  return packagePickerOrder;
};

export const useRecommendedPackageIndex = (): number => {
  const packageConfig = useSelector(getPackageConfig);
  const packagePickerOrder = usePackagePickerOrder();

  const recommendedPackageIndex = useMemo(() => {
    const recommendedPackageType = getRecommendedPackageType(packageConfig);
    return packagePickerOrder.findIndex(
      coreType => coreType === recommendedPackageType
    );
  }, [packageConfig, packagePickerOrder]);

  return recommendedPackageIndex;
};

export const useRecommendedPackage = (): CoreTypes | undefined => {
  const packagePickerOrder = usePackagePickerOrder();
  const recommendedPackageIndex = useRecommendedPackageIndex();

  return packagePickerOrder?.[recommendedPackageIndex];
};
